/**
 * A thin fetch wrapper for the staff API. Same-origin in production; the Vite dev proxy
 * (vite.config.ts) makes it same-origin in development too, so cookies behave identically in
 * both — no CORS credentials dance to get right twice.
 */
export interface ApiErrorBody {
  error: { code: string; message: string; details?: unknown; requestId: string };
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string;

  constructor(status: number, body: ApiErrorBody) {
    super(body.error.message);
    this.name = "ApiError";
    this.code = body.error.code;
    this.status = status;
    this.requestId = body.error.requestId;
  }
}

let csrfToken: string | undefined;

/** Set once, from GET /auth/me's response — see AuthContext. */
export const setCsrfToken = (token: string | undefined): void => {
  csrfToken = token;
};

const SAFE_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

/**
 * `rawResponse` hands back the `Response` untouched instead of parsing a JSON body.
 *
 * Exports need it: a small export answers with the file itself, and a large one answers 202 with a
 * job id, so the caller has to look at the status before deciding how to read the body. Everything
 * else in the app should keep using the parsed form.
 */
export interface ApiFetchInit extends RequestInit {
  rawResponse?: boolean;
}

export const apiFetch = async <T>(path: string, init: ApiFetchInit = {}): Promise<T> => {
  const method = (init.method ?? "GET").toUpperCase();
  const headers = new Headers(init.headers);

  // A FormData body must keep the browser's own multipart boundary in its content-type — setting
  // "application/json" on top of it would make the server parse a file upload as JSON and fail.
  if (init.body && !(init.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  if (!SAFE_METHODS.has(method) && csrfToken) headers.set("x-csrf-token", csrfToken);

  const { rawResponse, ...requestInit } = init;

  const response = await fetch(`/api/v1${path}`, {
    ...requestInit,
    method,
    headers,
    credentials: "include",
  });

  if (rawResponse) {
    if (!response.ok) {
      const errorBody = response.headers.get("content-type")?.includes("application/json")
        ? ((await response.json()) as ApiErrorBody)
        : undefined;
      throw new ApiError(response.status, errorBody as ApiErrorBody);
    }
    return response as unknown as T;
  }

  const isJson = response.headers.get("content-type")?.includes("application/json");
  const body = isJson ? await response.json() : undefined;

  if (!response.ok) {
    throw new ApiError(response.status, body as ApiErrorBody);
  }

  return body as T;
};

export const apiGet = <T>(path: string): Promise<T> => apiFetch<T>(path);

export const apiPost = <T>(path: string, data?: unknown): Promise<T> =>
  apiFetch<T>(path, {
    method: "POST",
    body: data === undefined ? undefined : JSON.stringify(data),
  });

export const apiPut = <T>(path: string, data?: unknown): Promise<T> =>
  apiFetch<T>(path, {
    method: "PUT",
    body: data === undefined ? undefined : JSON.stringify(data),
  });

export const apiPatch = <T>(path: string, data?: unknown): Promise<T> =>
  apiFetch<T>(path, {
    method: "PATCH",
    body: data === undefined ? undefined : JSON.stringify(data),
  });

export const apiDelete = <T>(path: string): Promise<T> => apiFetch<T>(path, { method: "DELETE" });

export const apiUpload = <T>(path: string, formData: FormData): Promise<T> =>
  apiFetch<T>(path, { method: "POST", body: formData });
